import { useCallback, useState } from "react";
import EndPoints from "../config/EndPoint";
import useFetch, { ServiceParams, ServiceResponse, refreshSession } from "../hooks/useFetch";
import {
  ServiceEmailRequestPayload,
  ServiceEmailRequestSubmitData,
  ServiceEmailTagsPayload,
} from "../models/ServiceEmailRequestModel";

type Events = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;

const qs = (filters?: Record<string, any>) => {
  if (!filters) return "";
  return Object.entries(filters)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join("&");
};

const fetchWithAuth = async (url: string, init: RequestInit): Promise<Response> => {
  const withCreds: RequestInit = { ...init, credentials: "include" };
  let response = await fetch(url, withCreds);
  if (response.status === 401) {
    const ok = await refreshSession();
    if (ok) {
      response = await fetch(url, withCreds);
    }
  }
  return response;
};

const useServiceEmailsService = () => {
  const { doGet, doPost, doPut } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFlag = useCallback((key: string, value: boolean) => {
    setFetching((prev) => ({ ...prev, [key]: value }));
  }, []);

  const list = useCallback(
    (filters?: Record<string, any>, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: `${EndPoints.SERVICES.EMAILS_LIST}${qs(filters) ? `?${qs(filters)}` : ""}`,
        setFetching: (value) => setFlag("list", value),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  const kpis = useCallback(
    (filters?: Record<string, any>, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: `${EndPoints.SERVICES.EMAILS_KPIS}${qs(filters) ? `?${qs(filters)}` : ""}`,
        setFetching: (value) => setFlag("kpis", value),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  const downloadSimpleExcel = useCallback(
    async (filters?: Record<string, any>, events: Events = {}) => {
      setFlag("downloadSimple", true);
      try {
        const query = qs(filters);
        const url = `${EndPoints.SERVICES.EMAILS_DOWNLOAD_SIMPLE}${query ? `?${query}` : ""}`;
        const response = await fetchWithAuth(url, {
          method: "GET",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json, */*",
          },
        });

        const contentType = (response.headers.get("Content-Type") || "").toLowerCase();
        if (!response.ok || contentType.includes("application/json")) {
          const errorData = await response.json().catch(() => ({}));
          const errMsg =
            (typeof errorData.message === "string" && errorData.message) ||
            (typeof errorData.errors === "string" && errorData.errors) ||
            "Error al descargar el Excel de correos";
          events.onError?.({
            message: errMsg,
            http_status: response.status,
          });
          return;
        }

        const blob = await response.blob();
        if (!blob || blob.size < 100) {
          events.onError?.({ message: "El Excel generado está vacío" });
          return;
        }

        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = `correos_simple.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/i);
          if (match?.[1]) filename = match[1];
        }

        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = urlBlob;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(urlBlob);
        events.onSuccess?.({ message: "Excel descargado" });
      } catch (error: any) {
        events.onError?.({ message: error?.message || "Error al descargar el Excel" });
      } finally {
        setFlag("downloadSimple", false);
      }
    },
    [setFlag]
  );

  const senderOptions = useCallback(
    (filters?: Record<string, any>, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: `${EndPoints.SERVICES.EMAILS_SENDER_OPTIONS}${qs(filters) ? `?${qs(filters)}` : ""}`,
        setFetching: (value) => setFlag("senderOptions", value),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFlag]
  );

  const store = useCallback(
    (payload: ServiceEmailRequestPayload | ServiceEmailRequestSubmitData, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.EMAILS_STORE,
        body: payload,
        setFetching: (value) => setFlag("store", value),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  const update = useCallback(
    (id: number, payload: ServiceEmailRequestPayload, events: Events = {}) =>
      doPut({
        url: EndPoints.SERVICES.EMAILS_UPDATE.replace(":id", String(id)),
        body: payload,
        setFetching: (value) => setFlag("update", value),
        ...events,
      } as ServiceParams),
    [doPut, setFlag]
  );

  const markAdded = useCallback(
    (id: number, payload: { service?: number | null; status?: string } = {}, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.EMAILS_MARK_ADDED.replace(":id", String(id)),
        body: payload,
        setFetching: (value) => setFlag("markAdded", value),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  const updateStatus = useCallback(
    (
      id: number,
      status: "PENDIENTE" | "AGREGADO",
      payload: { service?: number | null } = {},
      events: Events = {},
    ) =>
      markAdded(id, { ...payload, status }, events),
    [markAdded],
  );

  const updateTags = useCallback(
    (id: number, payload: ServiceEmailTagsPayload, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.EMAILS_TAGS.replace(":id", String(id)),
        body: payload,
        setFetching: (value) => setFlag("updateTags", value),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  const updateCompany = useCallback(
    (id: number, payload: { company: number | null }, events: Events = {}) =>
      doPost({
        url: EndPoints.SERVICES.EMAILS_COMPANY.replace(":id", String(id)),
        body: payload,
        setFetching: (value) => setFlag("updateCompany", value),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  const updateReceivedAt = useCallback(
    (
      id: number,
      payload: { time?: string; received_at?: string },
      events: Events = {}
    ) =>
      doPost({
        url: EndPoints.SERVICES.EMAILS_RECEIVED_AT.replace(":id", String(id)),
        body: payload,
        setFetching: (value) => setFlag("updateReceivedAt", value),
        ...events,
      } as ServiceParams),
    [doPost, setFlag]
  );

  return {
    fetching,
    list,
    kpis,
    downloadSimpleExcel,
    senderOptions,
    store,
    update,
    markAdded,
    updateStatus,
    updateReceivedAt,
    updateTags,
    updateCompany,
  };
};

export default useServiceEmailsService;
